/* global G */
/* eslint-disable object-curly-newline,no-unused-vars */
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Badge, IconButton, ListItem } from '@mui/material'
import { isArr } from 'lib/util'
import { useMemoRef } from '@platform/react/hook'
import ApplicationContext from 'platform/react/context/application'
import FloatingMenu from 'ui/Element/Acl/Item/NotificationMenu/Menu'
import Icon from 'ui/Element/Icon'

/**
 * Helper function to return a event handler that can optionally
 * execute a callback.
 *
 * @returns {function(*, {handler?: null, callback?: null}): *}
 */
const useHandler = () => useCallback((event, { handler = null, callback = null }) => {
  event.preventDefault()
  event.stopPropagation()

  const result = handler?.(event)
  callback?.()

  return result ?? undefined
}, [])

/**
 * Simple, ACL-controlled {@link Menu} meant to be used for links rendered in the
 * {@link Toolbar} that make use of the notification adapter.
 *
 * It gets rendered by the {@link notification} HOC, passing it the {@param} group.
 * The group adapters `G.API` will be used to get the various events needed.
 *
 * The current state of the notification will be passes as `notification`.
 * The `open` state of the menu is controlled by the adapter and passed via
 * the `notification.anchor` prop.
 *
 * @param {Object} child            various options for the icon
 * @param {Object} [options]        additional options
 * @param {Object} notification     state of the notification
 * @param {string} group            the name of the group
 * @param {Object} props            additional props
 * @returns {JSX.Element}
 */
const NotificationMenu = ({ child, options, notification, group, ...props }) => {
  const {
    key,
    label,
    noDataLabel,
    openInNewTabLabel,
    description,
    active,
    disabled,
    icon: iconProps,
    color,
    classes,
    openInNewTab = false,
  } = child

  const { session: { [G.MODULE]: currentModule } } = useContext(ApplicationContext)
  const { [G.GROUP]: { [group]: adapter } } = currentModule[G.ADAPTER][G.NOTIFICATION]
  const { [G.INTL]: intlAdapter, [G.EVENTS]: eventBus } = currentModule[G.ADAPTER]
  const [data, setData] = useState(null)
  const [updateKey, forceUpdate] = useState(0)

  const {
    toggle,
    read: onOpen,
    update: onUpdate,
    delete: onDelete,
    deleteAll: onDeleteAll,
    updateAll: onUpdateAll,
    set: onPage,
    show: onShow,
  } = adapter?.[G.API] || {}

  const listEvents = adapter?.[G.STATE]?.[G.LIST]?.[G.EVENTS] || null

  const { update, updateReverse, updateAll, delete: del, deleteAll } = useMemo(() => intlAdapter._t(
    'actions', {
      ns: 'common',
      _key: 'actions',
      returnObjects: true,
      defaultValue: {},
    },
  ), [])

  const {
    data: startData,
    page,
    count: unreadCount,
    update: updateList,
    reload,
    anchor = null,
  } = notification

  useEffect(() => {
    setData(updateList && isArr(startData) ? startData : data)
  }, [])

  const count = data && isArr(data) ? data.length : 0

  const menuOptions = { count, unreadCount }
  const listOptions = {
    data,
    page,
    update: updateList,
    reload,
    type: group,
    documentType: options?.notification?.documentType,
    openInNewTab,
  }

  const handler = useHandler()
  const handleClick = event => handler(event, { callback: () => { toggle?.(event.currentTarget) } })
  const handleClose = event => handler(event, { callback: () => { toggle?.(null) } })
  const handleShow = event => handler(event, { handler: onShow })

  const rerender = useCallback(() => {
    setData(null)
    forceUpdate(prevState => prevState + 1)
  }, [])

  useEffect(() => {
    const eventType = eventBus.type(G.NOTIFICATION, G.SET, group)

    eventBus.add(eventType, rerender)
    return () => eventBus.remove(eventType, rerender)
  }, [rerender])

  const icon = <Icon
    source={iconProps || 'bell'}
    color={color || 'black.main'}
  />

  return props.hidden ? null : (
    <ListItem
      key={key}
      className={`${classes.toolbarLinksItem}`}
    >
      <FloatingMenu
        key={updateKey}
        open={Boolean(anchor)}
        anchor={anchor}
        listOptions={listOptions}
        menuOptions={menuOptions}
        events={{
          onOpen,
          onUpdate,
          onUpdateAll,
          onDelete,
          onDeleteAll,
          onPage,
          ...listEvents && { listEvents },
          onShow: handleShow,
          onClick: handleClick,
          onClose: handleClose,
        }}
        labels={{
          title: label,
          noData: noDataLabel,
          openInNewTabLabel,
          description,
          update,
          updateReverse,
          updateAll,
          del,
          deleteAll,
        }}
      />
      <IconButton
        size={'small'}
        onClick={handleClick}
        disabled={disabled || active}
      >
        {unreadCount > 0
          ? (<Badge
            badgeContent={unreadCount}
            classes={{ badge: classes.toolbarLinksItemBadge }}
          >
            {icon}
          </Badge>)
          : icon
        }
      </IconButton>
    </ListItem>
  )
}

export default useMemoRef(NotificationMenu, false)
